import { useEffect } from "react";
import { useSite } from "@/context/SiteContext";
import { resolveMedia } from "@/lib/media";

export const THEME_VAR_MAP = {
  bg: "--aurex-bg",
  surface: "--aurex-surface",
  surface_2: "--aurex-surface-2",
  text: "--aurex-text",
  muted: "--aurex-muted",
  accent: "--aurex-accent",
  accent_soft: "--aurex-accent-soft",
  accent_2: "--aurex-accent-2",
  on_accent: "--aurex-on-accent",
};

export function applyTheme(theme) {
  if (!theme) return;
  const root = document.documentElement;
  Object.entries(THEME_VAR_MAP).forEach(([key, cssVar]) => {
    if (theme[key]) root.style.setProperty(cssVar, theme[key]);
  });
}

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => {
      if (k !== "content") el.setAttribute(k, v);
    });
    document.head.appendChild(el);
  }
  if (attrs.content != null) el.setAttribute("content", attrs.content);
}

export default function ThemeInjector() {
  const { content } = useSite();
  useEffect(() => {
    applyTheme(content?.theme);
    // dynamic favicon
    const fav = resolveMedia(content?.brand?.favicon_url);
    if (fav) {
      document.querySelectorAll("link[rel='icon'], link[rel='apple-touch-icon']").forEach((l) => {
        l.setAttribute("href", fav);
      });
      let icon = document.querySelector("link[rel='icon']");
      if (!icon) {
        icon = document.createElement("link");
        icon.rel = "icon";
        icon.href = fav;
        document.head.appendChild(icon);
      }
    }
    // SEO: title, description, keywords, Open Graph
    const seo = content?.seo || {};
    const name = content?.brand?.name;
    const tagline = content?.brand?.tagline;
    const title = seo.title || (name ? (tagline ? `${name} — ${tagline}` : name) : document.title);
    if (title) document.title = title;
    if (seo.description) {
      upsertMeta("meta[name='description']", { name: "description", content: seo.description });
      upsertMeta("meta[property='og:description']", { property: "og:description", content: seo.description });
      upsertMeta("meta[name='twitter:description']", { name: "twitter:description", content: seo.description });
    }
    if (seo.keywords) upsertMeta("meta[name='keywords']", { name: "keywords", content: seo.keywords });
    upsertMeta("meta[property='og:title']", { property: "og:title", content: title });
    upsertMeta("meta[name='twitter:title']", { name: "twitter:title", content: title });
    upsertMeta("meta[property='og:type']", { property: "og:type", content: "website" });
    const ogImg = resolveMedia(seo.og_image || content?.hero?.image);
    if (ogImg) {
      upsertMeta("meta[property='og:image']", { property: "og:image", content: ogImg });
      upsertMeta("meta[name='twitter:image']", { name: "twitter:image", content: ogImg });
      upsertMeta("meta[name='twitter:card']", { name: "twitter:card", content: "summary_large_image" });
    }
    // Dynamic canonical + og:url when the user configures their site URL
    if (seo.site_url) {
      const base = seo.site_url.replace(/\/+$/, "");
      const canonical = base + (window.location.pathname === "/" ? "/" : window.location.pathname);
      let link = document.querySelector("link[rel='canonical']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
      upsertMeta("meta[property='og:url']", { property: "og:url", content: canonical });
    }
  }, [content]);
  return null;
}
